import React from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import Button from "@components/Button";
import Input from "@components/Input";
import SizedBox from "@components/SizedBox";
import { ROUTES } from "@stores/AccountStore";
import { observer } from "mobx-react-lite";
import { useLang } from "@src/hooks/useLang";
import AuthLayout, { ErrorAlert } from "@components/AuthLayout";
import { authClient } from "@services/authService";
import Text from "@components/Text";
import { toast } from "react-toastify";

/** Set a new password from the emailed reset link (/password-reset?token=...). */
const PasswordResetScreen: React.FC = () => {

  const lang = useLang();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const t = (lang as any).auth ?? {};

  const [password, setPassword] = React.useState("");
  const [repeatPassword, setRepeatPassword] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);

  // No token → the link is broken/incomplete; send them to request a new one.
  if (!token) {
    return <Navigate to={ROUTES.PASSWORD_FORGOT} />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password !== repeatPassword) {
      setError(t.passwordsDontMatch ?? "Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      const { error } = await authClient.resetPassword({
        newPassword: password,
        token,
      });
      if (error) {
        // Typically an expired/used token — point them back to the forgot flow.
        setError(error.message || "Password reset failed");
        setLoading(false);
        return;
      }
      toast.success(t.passwordResetSuccess ?? "Password updated. Please sign in.");
      navigate(ROUTES.LOGIN);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Password reset failed");
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <form onSubmit={handleSubmit} style={{ width: 336 }}>
        <ErrorAlert enabled={!!error}>{error}</ErrorAlert>
        <Text size={18} weight={500} style={{ textAlign: "center", width: "100%" }}>
          {t.restoreTitle ?? "Restore password"}
        </Text>
        <SizedBox height={16} />
        <Input
          label={t.newPasswordTitle ?? "New password"}
          placeholder={t.newPasswordField ?? "Enter password"}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          required
        />
        <SizedBox height={16} />
        <Input
          label={t.repeatPasswordTitle ?? "Repeat password"}
          placeholder={t.repeatPasswordField ?? "Enter password"}
          type="password"
          value={repeatPassword}
          onChange={(e) => setRepeatPassword(e.target.value)}
          autoComplete="new-password"
          required
        />
        <SizedBox height={24} />
        <Button type="submit" disabled={loading}>
          {t.saveNewPasswordButton ?? "Save"}
        </Button>
        <SizedBox height={16} />
        <Text
          size={14}
          color="#68AD18"
          style={{ cursor: "pointer", textAlign: "center", width: "100%" }}
          onClick={() => navigate(ROUTES.PASSWORD_FORGOT)}
        >
          {t.restorePasswordButton ?? "Reset password"}
        </Text>
      </form>
    </AuthLayout>
  );
};

export default observer(PasswordResetScreen);
